import axios from 'axios';

// Production (served by Express) must use same-origin /api.
// Dev defaults to local API; override with REACT_APP_API_URL when needed.
const rawApiUrl =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5001/api');
const API_BASE_URL = rawApiUrl.replace(/\/$/, '').replace(/\/api$/i, '') + '/api';

const client = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const unwrap = (error) => {
  throw error.response?.data || { error: error.message };
};

export const registerUser = async (payload) => {
  try {
    const res = await client.post('/register', payload);
    return res.data;
  } catch (error) {
    unwrap(error);
  }
};

export const loginUser = async (email, password) => {
  try {
    const res = await client.post('/login', { email, password });
    return res.data;
  } catch (error) {
    unwrap(error);
  }
};

export const activateAccount = async (token) => {
  try {
    const res = await client.post('/activate', { token });
    return res.data;
  } catch (error) {
    unwrap(error);
  }
};

export const requestPasswordReset = async (email) => {
  try {
    const res = await client.post('/forgot-password', { email });
    return res.data;
  } catch (error) {
    unwrap(error);
  }
};

export const resetPassword = async (token, password) => {
  try {
    const res = await client.post('/reset-password', { token, password });
    return res.data;
  } catch (error) {
    unwrap(error);
  }
};

export const getRegistrationOptions = async () => {
  try {
    const res = await client.get('/registration-options');
    const data = res.data;
    if (!data || !Array.isArray(data.genders) || !Array.isArray(data.races)) {
      throw new Error('Invalid registration options response');
    }
    return data;
  } catch (error) {
    unwrap(error);
  }
};

export const updateAccount = async (payload) => {
  try {
    const res = await client.put('/account', payload);
    return res.data;
  } catch (error) {
    unwrap(error);
  }
};

export const updatePreferences = async (payload) => {
  try {
    const res = await client.put('/preferences', payload);
    return res.data;
  } catch (error) {
    unwrap(error);
  }
};

export const getAchievements = async (userId, timeZone) => {
  try {
    const res = await client.get('/achievements', {
      params: { userId, timeZone: timeZone || undefined },
    });
    return res.data;
  } catch (error) {
    unwrap(error);
  }
};

export const getConsumptionOptions = async () => {
  try {
    const res = await client.get('/consumption-options');
    return res.data;
  } catch (error) {
    unwrap(error);
  }
};

export const getStrains = async () => {
  try {
    const res = await client.get('/strains');
    return res.data;
  } catch (error) {
    unwrap(error);
  }
};

export const getBrands = async () => {
  try {
    const res = await client.get('/brands');
    return res.data;
  } catch (error) {
    unwrap(error);
  }
};

export const getAllowedEmails = async (userId) => {
  try {
    const res = await client.get('/allowed-emails', { params: { userId } });
    return res.data;
  } catch (error) {
    unwrap(error);
  }
};

export const addAllowedEmail = async (userId, email) => {
  try {
    const res = await client.post('/allowed-emails', { userId, email });
    return res.data;
  } catch (error) {
    unwrap(error);
  }
};

export const removeAllowedEmail = async (userId, email) => {
  try {
    const res = await client.delete('/allowed-emails', { data: { userId, email } });
    return res.data;
  } catch (error) {
    unwrap(error);
  }
};

export const getStatsAccess = async (userId) => {
  try {
    const res = await client.get('/stats/access', { params: { userId } });
    return res.data;
  } catch (error) {
    unwrap(error);
  }
};

export const getAdminSchema = async (userId) => {
  try {
    const res = await client.get('/admin/schema', { params: { userId } });
    return res.data;
  } catch (error) {
    unwrap(error);
  }
};

export const runAdminQuery = async (userId, sql, limit) => {
  try {
    const res = await client.post('/admin/query', { userId, sql, limit });
    return res.data;
  } catch (error) {
    unwrap(error);
  }
};

export const getDashboardStats = async (userId) => {
  try {
    const res = await client.get('/stats/dashboard', { params: { userId } });
    return res.data;
  } catch (error) {
    unwrap(error);
  }
};

export const setAlphaTesterAchievement = async (userId, enabled) => {
  try {
    const res = await client.put('/stats/alpha-tester', { userId, enabled });
    return res.data;
  } catch (error) {
    unwrap(error);
  }
};

const browserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (error) {
    return '';
  }
};

export const getLogs = async (userId, strain, date) => {
  try {
    const res = await client.get('/logs', {
      params: {
        userId,
        ...(strain ? { strain } : {}),
        ...(date ? { date, timeZone: browserTimeZone() } : {}),
      },
    });
    return res.data;
  } catch (error) {
    unwrap(error);
  }
};

export const getSessionDates = async (userId) => {
  try {
    const res = await client.get('/logs/dates', {
      params: { userId, timeZone: browserTimeZone() },
    });
    return res.data;
  } catch (error) {
    unwrap(error);
  }
};

export const getReviewedStrains = async (userId) => {
  try {
    const res = await client.get('/logs/strains', { params: { userId } });
    return res.data;
  } catch (error) {
    unwrap(error);
  }
};

export const getFriends = async (userId) => {
  try {
    const res = await client.get('/friends', { params: { userId } });
    return res.data;
  } catch (error) {
    unwrap(error);
  }
};

export const getFriendCode = async (userId) => {
  try {
    const res = await client.get('/friends/code', { params: { userId } });
    return res.data;
  } catch (error) {
    unwrap(error);
  }
};

export const addFriendByCode = async (userId, code) => {
  try {
    const res = await client.post('/friends/add', { userId, code });
    return res.data;
  } catch (error) {
    unwrap(error);
  }
};

export const getFriendLogs = async (userId, strain) => {
  try {
    const res = await client.get('/friends/logs', {
      params: { userId, ...(strain ? { strain } : {}) },
    });
    return res.data;
  } catch (error) {
    unwrap(error);
  }
};

export const getFriendStrains = async (userId) => {
  try {
    const res = await client.get('/friends/strains', { params: { userId } });
    return res.data;
  } catch (error) {
    unwrap(error);
  }
};

export const updateLog = async (logId, payload) => {
  try {
    const res = await client.patch(`/logs/${logId}`, payload);
    return res.data;
  } catch (error) {
    unwrap(error);
  }
};

export const saveConsumptionLog = async (payload) => {
  try {
    const res = await client.post('/logs', payload);
    return res.data;
  } catch (error) {
    unwrap(error);
  }
};
